/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * HyusnSu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Thu Mar 23 2023
 **/

import React, { createContext, useState } from "react";
import { createPortal } from "react-dom";
import { v4 } from "uuid";
import has from "lodash-es/has";
import { Slide, Snackbar, SxProps, Theme } from "@mui/material";

import Toast from "./Toast";
import { withProcedure, wrappedPromise } from "@/frameworks/core/components/Procedure";
import { useEventHandlers } from "@rfice/eventhandler";
import useToast from "./useToast";

interface ToastHandlers {
	handleShow: ({
		id,
		type,
		message,
		closeButton,
		autoHide,
		permanent
	}: {
		id?: string;
		type: DesignStuff.Toast.Type;
		message: string;
		closeButton?: boolean;
		autoHide?: boolean;
		permanent?: boolean;
	}) => void;
	handleHide: (id: string) => void;
}

export const ToastStateContext = createContext<DesignStuff.Toast.ToastItem[]>([]);
export const ToastActionContext = createContext<ToastHandlers | undefined>(undefined);

interface ToastContainerProps {
	children: React.ReactNode;
}

export default function ToastContainer({ children }: ToastContainerProps) {
	const [toasts, setToasts] = useState<DesignStuff.Toast.ToastItem[]>([]);

	const handleShow = ({
		type,
		message,
		closeButton,
		permanent,
		...rest
	}: {
		id?: string;
		type: DesignStuff.Toast.Type;
		message: string;
		closeButton?: boolean;
		autoHide?: boolean;
		permanent?: boolean;
	}) => {
		const id = rest.id ?? v4();
		const item = { id, type, message, showCloseButton: closeButton, autoHide: rest.autoHide ?? true, permanent };

		setToasts((prev) => (prev.length > 0 ? [...prev, item] : [item]));
	};

	const handleHide = (id: string) => {
		setToasts((prev) => {
			return prev.filter((item) => item.id !== id);
		});
	};

	const snackbarSx: SxProps<Theme> = {
		zIndex: 13000
	};

	return (
		<>
			<ToastStateContext.Provider value={toasts}>
				<ToastActionContext.Provider
					value={{
						handleShow,
						handleHide
					}}
				>
					<ToastContainerChildren
						children={children}
						handlers={{
							handleShow,
							handleHide
						}}
					/>
				</ToastActionContext.Provider>
			</ToastStateContext.Provider>

			{createPortal(
				<React.Fragment>
					{toasts?.map((item: DesignStuff.Toast.ToastItem) => {
						const autoHide = has(item, "autoHide") ? (item.autoHide ? 2000 : null) : 2000;
						return (
							<Snackbar
								key={item.id}
								{...(!item.permanent && {
									autoHideDuration:
										item.type === "error" ? 3500 : item.type === "fatal" ? null : autoHide,
									onClose: () => handleHide(item.id)
								})}
								open={true}
								anchorOrigin={{ vertical: "top", horizontal: "center" }}
								TransitionComponent={Slide}
								sx={snackbarSx}
							>
								<div>
									<Toast
										status={item.type}
										message={item.message}
										showCloseButton={item.showCloseButton ?? true}
										onClose={() => handleHide(item.id)}
									/>
								</div>
							</Snackbar>
						);
					})}
				</React.Fragment>,
				document.body
			)}
		</>
	);
}

interface ChildrenProps {
	children: React.ReactNode;
	handlers: ToastHandlers;
}

function Children({ children }: ChildrenProps) {
	return <>{children}</>;
}

function withEventHandler(Component: React.ComponentType<ChildrenProps>) {
	return (props: ChildrenProps) => {
		const toast = useToast();

		useEventHandlers(
			{
				"Command::Toast": (...data) => {
					console.log("onCommandToast", data);
					const [command, payload] = data;
					switch (command) {
						case "handleShow": {
							toast.show(payload);
							break;
						}
						case "handleHide": {
							toast.hide(payload);
							break;
						}
					}
				}
			},
			[]
		);

		return <Component {...props} />;
	};
}
const ToastContainerChildren = withProcedure(withEventHandler(Children), () => {
	return wrappedPromise(
		new Promise((resolve) => {
			resolve(null);
		})
	);
});
